/**
 * PayrollBatchDetailPage — Issue #371.
 *
 * 工资批次详情页：批次审批 / 出纳付款 / hold 刷新 / 行级 reveal。
 * 状态完全由后端驱动；前端只做按钮 + 表单。
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useRoute } from 'wouter';
import { useAuth } from '@/hooks/useAuth';
import {
  FINANCE_PERMISSIONS,
  hasPermission,
} from '@/lib/financePermissions';
import {
  usePayrollBatchActions,
  usePayrollBatchDetail,
  usePayrollRowActions,
  usePayrollRows,
  type PayrollEmployeeRowSummaryDto,
} from '@/hooks/useFinancePayroll';

const STATUS_LABELS: Record<string, string> = {
  draft: '草稿',
  parsing: '解析中',
  parsed: '已解析',
  pending_hr_review: '待 HR 审核',
  pending_finance_review: '待财务复核',
  approved: '已批准',
  payout_in_progress: '出纳付款中',
  paid: '已发放',
  rejected: '已驳回',
  cancelled: '已撤回',
  failed: '解析失败',
};

const PAYOUT_STATUS_LABELS: Record<string, string> = {
  pending: '待发放',
  hold: 'Hold（已暂停）',
  released: '已释放，可付款',
  paid: '已发放',
  cancelled: '已取消',
};

const HOLD_REASON_LABELS: Record<string, string> = {
  hr_offboarding_payout_blocked: '#363 离职 hold：未交接',
  hr_offboarding_retained_blocked: '#363 离职 hold：扣留',
  hr_offboarding_pending_review: '#363 离职 hold：待 HR 复核',
  certificate_freeze: '#362 资质冻结',
  manual_hold: '人工暂停',
};

export default function PayrollBatchDetailPage() {
  const [, params] = useRoute('/finance/payroll/:id');
  const id = params?.id ?? '';
  const { permissions } = useAuth();
  const granted = permissions ?? [];
  const canHrReview = hasPermission(
    granted,
    FINANCE_PERMISSIONS.PAYROLL_HR_REVIEW,
  );
  const canFinanceReview = hasPermission(
    granted,
    FINANCE_PERMISSIONS.PAYROLL_FINANCE_REVIEW,
  );
  const canCashier = hasPermission(
    granted,
    FINANCE_PERMISSIONS.PAYROLL_CASHIER_PAYOUT,
  );
  const canManage = hasPermission(granted, FINANCE_PERMISSIONS.PAYROLL_MANAGE);
  const canReveal = hasPermission(granted, FINANCE_PERMISSIONS.PAYROLL_REVEAL);
  const canImport = hasPermission(granted, FINANCE_PERMISSIONS.PAYROLL_IMPORT);

  const { data: batch, loading, error, fetchDetail } = usePayrollBatchDetail();
  const { data: rows, fetchList: fetchRows } = usePayrollRows();
  const batchActions = usePayrollBatchActions();
  const rowActions = usePayrollRowActions();
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  type RevealField =
    | 'id_card_number'
    | 'social_insurance_number'
    | 'gross_amount'
    | 'net_amount'
    | 'tax_amount'
    | 'social_insurance_amount'
    | 'housing_fund_amount';
  const [revealing, setRevealing] = useState<{
    rowId: string;
    field: RevealField;
  } | null>(null);
  const [revealReason, setRevealReason] = useState('');
  const [revealContext, setRevealContext] = useState('');
  const [revealResult, setRevealResult] = useState<{
    field: string;
    value: string;
    auditId: string;
  } | null>(null);

  useEffect(() => {
    if (id) void fetchDetail(id);
  }, [id, fetchDetail]);

  useEffect(() => {
    if (id) void fetchRows({ batchId: id, page: 1, limit: 200 });
  }, [id, fetchRows]);

  const refresh = async () => {
    await fetchDetail(id);
    await fetchRows({ batchId: id, page: 1, limit: 200 });
  };

  const handleAction = async (
    promise: Promise<unknown>,
    successText: string,
  ) => {
    setActionMessage(null);
    const result = await promise;
    if (result) {
      setActionMessage(successText);
      await refresh();
    }
  };

  const handleReveal = async () => {
    if (!revealing) return;
    const result = await rowActions.reveal(revealing.rowId, {
      field: revealing.field,
      reason: revealReason.trim(),
      businessContext: revealContext.trim(),
    });
    if (result) {
      setRevealResult({
        field: result.field,
        value: result.plaintext ?? result.maskValue ?? '—',
        auditId: result.auditId,
      });
      setRevealReason('');
      setRevealContext('');
    }
  };

  /**
   * Cashier-side voucher attach + pay flow (Issue #371 audit fix).
   *
   * The cashier must produce the payout voucher before pressing the actual
   * "付款" button. We open a small modal that:
   *   1) lets the cashier pick the voucher file (PDF / image / xlsx),
   *   2) uploads it through `/rows/:id/voucher` to private OSS via the
   *      backend, receiving an opaque `voucherFileId`,
   *   3) submits `/pay` with that id; the backend validates the id is a
   *      `voucher` file scoped to this same tenant / batch / row before
   *      writing the immutable payout record.
   *
   * The first-phase policy keeps voucher attachment OPTIONAL for cash and
   * legacy bank flows; cashier may press "无凭证直接付款" only after
   * explicit confirmation. The bucket / storage key / signed URL never enter
   * UI state — only the opaque id + audit metadata.
   */
  const [payingRow, setPayingRow] =
    useState<PayrollEmployeeRowSummaryDto | null>(null);
  const [payVoucherFile, setPayVoucherFile] = useState<File | null>(null);
  const [payVoucherUploaded, setPayVoucherUploaded] = useState<{
    voucherFileId: string;
    originalName: string | null;
    byteSize: string;
  } | null>(null);
  const [payPaymentMethod, setPayPaymentMethod] = useState<
    'bank_transfer' | 'cash' | 'check' | 'wechat' | 'alipay' | 'other'
  >('bank_transfer');
  const [payReference, setPayReference] = useState('');
  const [payRemarks, setPayRemarks] = useState('');
  const [payConfirmNoVoucher, setPayConfirmNoVoucher] = useState(false);
  const payFileInputRef = useRef<HTMLInputElement | null>(null);

  const openPayDialog = (row: PayrollEmployeeRowSummaryDto) => {
    setPayingRow(row);
    setPayVoucherFile(null);
    setPayVoucherUploaded(null);
    setPayPaymentMethod('bank_transfer');
    setPayReference('');
    setPayRemarks('');
    setPayConfirmNoVoucher(false);
    if (payFileInputRef.current) payFileInputRef.current.value = '';
  };
  const closePayDialog = () => {
    setPayingRow(null);
    setPayVoucherFile(null);
    setPayVoucherUploaded(null);
    setPayReference('');
    setPayRemarks('');
    setPayConfirmNoVoucher(false);
  };

  const handleUploadVoucher = async () => {
    if (!payingRow || !payVoucherFile) return;
    const file = await rowActions.uploadPayoutVoucher({
      rowId: payingRow.id,
      batchId: payingRow.batchId,
      file: payVoucherFile,
    });
    if (file) {
      setPayVoucherUploaded({
        voucherFileId: file.id,
        originalName: file.originalName ?? null,
        byteSize: file.byteSize,
      });
    }
  };

  const handleConfirmPay = async () => {
    if (!payingRow) return;
    const today = new Date().toISOString().slice(0, 10);
    const result = await rowActions.payRow(payingRow.id, {
      expectedRecordVersion: payingRow.recordVersion,
      paymentMethod: payPaymentMethod,
      executedAt: today,
      ...(payReference.trim() ? { referenceNumber: payReference.trim() } : {}),
      ...(payRemarks.trim() ? { remarks: payRemarks.trim() } : {}),
      ...(payVoucherUploaded
        ? { voucherFileId: payVoucherUploaded.voucherFileId }
        : {}),
    });
    if (result) {
      setActionMessage(
        `已记账：${payingRow.employeeName} (#${payingRow.rowNumber})`,
      );
      closePayDialog();
      await refresh();
    }
  };

  const releaseHold = async (row: PayrollEmployeeRowSummaryDto) => {
    await handleAction(
      rowActions.releaseHold(row.id, {
        expectedRecordVersion: row.recordVersion,
      }),
      `已释放：${row.employeeName}`,
    );
  };

  const totalsByStatus = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const row of rows?.rows ?? []) {
      counts[row.payoutStatus] = (counts[row.payoutStatus] ?? 0) + 1;
    }
    return counts;
  }, [rows]);

  if (!id) return <div className="p-6">缺少批次 id</div>;

  return (
    <div className="space-y-4 p-6">
      <h1 className="text-xl font-semibold">
        工资批次 {batch?.batchNo ?? id}
      </h1>
      {loading && <div className="text-sm text-gray-500">加载中…</div>}
      {error && (
        <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          加载失败：{error.message}
        </div>
      )}
      {actionMessage && (
        <div className="rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">
          {actionMessage}
        </div>
      )}
      {(batchActions.actionError || rowActions.actionError) && (
        <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          操作失败：
          {batchActions.actionError?.message ?? rowActions.actionError?.message}
        </div>
      )}

      {batch && (
        <div className="rounded-md border border-gray-200 bg-white p-4">
          <div className="grid grid-cols-2 gap-y-2 text-sm sm:grid-cols-3">
            <div>
              <span className="text-gray-500">期间：</span>
              {batch.periodYear} / {String(batch.periodMonth).padStart(2, '0')}
            </div>
            <div>
              <span className="text-gray-500">状态：</span>
              {STATUS_LABELS[batch.status] ?? batch.status}
            </div>
            <div>
              <span className="text-gray-500">来源：</span>
              {batch.sourceType}
            </div>
            <div>
              <span className="text-gray-500">应发合计（脱敏）：</span>
              {batch.totalGrossAmountMask}
            </div>
            <div>
              <span className="text-gray-500">实发合计（脱敏）：</span>
              {batch.totalNetAmountMask}
            </div>
            <div>
              <span className="text-gray-500">人数：</span>
              {batch.totalEmployeeCount}
            </div>
            <div>
              <span className="text-gray-500">错误数：</span>
              {batch.errorCount}
            </div>
            <div>
              <span className="text-gray-500">已发放：</span>
              {batch.paidCount}
            </div>
            <div>
              <span className="text-gray-500">Hold：</span>
              {batch.heldCount}
            </div>
            <div>
              <span className="text-gray-500">附表：</span>
              {(batch.providedSheetKeys ?? []).length}/10
            </div>
            {batch.rulesetVersion && (
              <div>
                <span className="text-gray-500">引擎版本：</span>
                {batch.rulesetVersion}
              </div>
            )}
          </div>
          <div className="mt-4 flex flex-wrap gap-2 border-t border-gray-100 pt-3">
            {batch.status === 'parsed' && canImport && (
              <button
                type="button"
                onClick={() =>
                  void handleAction(
                    batchActions.submitForHrReview(batch.id, batch.recordVersion),
                    '已提交 HR 审核',
                  )
                }
                className="rounded-md bg-blue-600 px-3 py-1.5 text-sm text-white"
              >
                提交 HR 审核
              </button>
            )}
            {batch.status === 'pending_hr_review' && canHrReview && (
              <button
                type="button"
                onClick={() =>
                  void handleAction(
                    batchActions.hrReview(batch.id, {
                      expectedRecordVersion: batch.recordVersion,
                    }),
                    'HR 审核通过',
                  )
                }
                className="rounded-md bg-blue-600 px-3 py-1.5 text-sm text-white"
              >
                HR 审核通过
              </button>
            )}
            {batch.status === 'pending_finance_review' && canFinanceReview && (
              <button
                type="button"
                onClick={() =>
                  void handleAction(
                    batchActions.financeReview(batch.id, {
                      expectedRecordVersion: batch.recordVersion,
                    }),
                    '财务复核通过',
                  )
                }
                className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm text-white"
              >
                财务复核通过
              </button>
            )}
            {batch.status === 'approved' && canCashier && (
              <button
                type="button"
                onClick={() =>
                  void handleAction(
                    batchActions.startPayout(batch.id, batch.recordVersion),
                    '出纳开始付款',
                  )
                }
                className="rounded-md bg-purple-600 px-3 py-1.5 text-sm text-white"
              >
                出纳开始付款
              </button>
            )}
            {batch.status === 'payout_in_progress' && canFinanceReview && (
              <button
                type="button"
                onClick={() =>
                  void handleAction(
                    batchActions.refreshHolds(batch.id),
                    '已重算 #363 hold',
                  )
                }
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm"
              >
                重算 #363 hold
              </button>
            )}
            {canManage && batch.status !== 'paid' && batch.status !== 'cancelled' && (
              <button
                type="button"
                onClick={() => {
                  const reason = window.prompt('撤销原因（必填，≥ 4 字符）');
                  if (!reason || reason.length < 4) return;
                  void handleAction(
                    batchActions.cancelBatch(batch.id, {
                      expectedRecordVersion: batch.recordVersion,
                      reason,
                    }),
                    '已撤销批次',
                  );
                }}
                className="rounded-md border border-rose-300 px-3 py-1.5 text-sm text-rose-700"
              >
                撤销批次
              </button>
            )}
          </div>
        </div>
      )}

      <div>
        <h2 className="mt-6 text-base font-semibold">员工薪资行</h2>
        <p className="text-xs text-gray-500">
          {Object.entries(totalsByStatus)
            .map(([k, v]) => `${PAYOUT_STATUS_LABELS[k] ?? k}: ${v}`)
            .join(' / ')}
        </p>
        <div className="mt-2 overflow-x-auto rounded-md border border-gray-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left">#</th>
                <th className="px-3 py-2 text-left">姓名</th>
                <th className="px-3 py-2 text-left">部门</th>
                <th className="px-3 py-2 text-left">职位</th>
                <th className="px-3 py-2 text-left">身份证（脱敏）</th>
                <th className="px-3 py-2 text-left">应发（脱敏）</th>
                <th className="px-3 py-2 text-left">实发（脱敏）</th>
                <th className="px-3 py-2 text-left">付款状态</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {(rows?.rows ?? []).map((row) => (
                <tr key={row.id} className="border-t border-gray-100">
                  <td className="px-3 py-2">{row.rowNumber}</td>
                  <td className="px-3 py-2">{row.employeeName}</td>
                  <td className="px-3 py-2">{row.department ?? '—'}</td>
                  <td className="px-3 py-2">{row.position ?? '—'}</td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {row.idCardMask ?? '—'}
                  </td>
                  <td className="px-3 py-2">{row.grossAmountMask}</td>
                  <td className="px-3 py-2">{row.netAmountMask}</td>
                  <td className="px-3 py-2">
                    <div>{PAYOUT_STATUS_LABELS[row.payoutStatus] ?? row.payoutStatus}</div>
                    {row.payoutHoldReason && (
                      <div className="text-xs text-amber-700">
                        {HOLD_REASON_LABELS[row.payoutHoldReason] ?? row.payoutHoldReason}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {canCashier &&
                        batch?.status === 'payout_in_progress' &&
                        (row.payoutStatus === 'released' ||
                          row.payoutStatus === 'pending') && (
                          <button
                            type="button"
                            onClick={() => openPayDialog(row)}
                            className="rounded-md bg-purple-600 px-2 py-0.5 text-xs text-white"
                          >
                            付款
                          </button>
                        )}
                      {canFinanceReview && row.payoutStatus === 'hold' && (
                        <button
                          type="button"
                          onClick={() => void releaseHold(row)}
                          className="rounded-md border border-gray-300 px-2 py-0.5 text-xs"
                        >
                          释放 hold
                        </button>
                      )}
                      {canReveal && (
                        <button
                          type="button"
                          onClick={() => {
                            setRevealing({ rowId: row.id, field: 'net_amount' });
                            setRevealResult(null);
                          }}
                          className="rounded-md border border-gray-300 px-2 py-0.5 text-xs"
                        >
                          查看明文
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {(rows?.rows ?? []).length === 0 && (
                <tr>
                  <td className="px-3 py-4 text-center text-gray-400" colSpan={9}>
                    暂无员工薪资行
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {revealing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-md space-y-3 rounded-md bg-white p-4">
            <h3 className="text-base font-semibold">查看薪资明文（受控）</h3>
            <p className="text-xs text-gray-600">
              字段：{revealing.field}；该操作将写入审计。
            </p>
            <label className="text-xs text-gray-600">
              原因（≥ 4 字符）
              <textarea
                value={revealReason}
                onChange={(e) => setRevealReason(e.target.value)}
                className="mt-1 h-16 w-full rounded-md border border-gray-300 p-2 text-xs"
              />
            </label>
            <label className="text-xs text-gray-600">
              业务上下文（≥ 4 字符）
              <textarea
                value={revealContext}
                onChange={(e) => setRevealContext(e.target.value)}
                className="mt-1 h-16 w-full rounded-md border border-gray-300 p-2 text-xs"
              />
            </label>
            {revealResult && (
              <div className="rounded-md bg-yellow-50 p-2 text-xs">
                <div>
                  字段：{revealResult.field}
                </div>
                <div className="break-all font-mono">
                  值：{revealResult.value}
                </div>
                <div className="text-gray-500">审计 id：{revealResult.auditId}</div>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setRevealing(null);
                  setRevealResult(null);
                  setRevealReason('');
                  setRevealContext('');
                }}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm"
              >
                关闭
              </button>
              <button
                type="button"
                disabled={
                  revealReason.trim().length < 4 ||
                  revealContext.trim().length < 4 ||
                  rowActions.actionLoading
                }
                onClick={() => void handleReveal()}
                className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm text-white disabled:opacity-50"
              >
                确认查看
              </button>
            </div>
          </div>
        </div>
      )}

      {payingRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-md space-y-3 rounded-md bg-white p-4">
            <h3 className="text-base font-semibold">
              付款执行：{payingRow.employeeName} (#{payingRow.rowNumber})
            </h3>
            <p className="text-xs text-gray-600">
              出纳付款前请先上传付款凭证（PDF / 图片 / xlsx）；上传后会获得后端校验过的不透明凭证 id。
              如本笔为现金等暂无凭证场景，请勾选「确认无凭证付款」。
            </p>
            <div className="space-y-1">
              <label className="text-xs text-gray-600">付款方式</label>
              <select
                value={payPaymentMethod}
                onChange={(e) =>
                  setPayPaymentMethod(
                    e.target.value as typeof payPaymentMethod,
                  )
                }
                className="w-full rounded-md border border-gray-300 px-2 py-1 text-xs"
              >
                <option value="bank_transfer">银行转账</option>
                <option value="cash">现金</option>
                <option value="check">支票</option>
                <option value="wechat">微信</option>
                <option value="alipay">支付宝</option>
                <option value="other">其他</option>
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-gray-600">付款凭证文件</label>
              <input
                ref={payFileInputRef}
                type="file"
                accept="application/pdf,image/png,image/jpeg,image/webp,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(e) => {
                  const f = e.target.files?.[0] ?? null;
                  setPayVoucherFile(f);
                  setPayVoucherUploaded(null);
                }}
                className="w-full text-xs"
              />
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={
                    !payVoucherFile ||
                    payVoucherUploaded !== null ||
                    rowActions.actionLoading
                  }
                  onClick={() => void handleUploadVoucher()}
                  className="rounded-md bg-blue-600 px-3 py-1 text-xs text-white disabled:opacity-50"
                >
                  上传凭证
                </button>
                {payVoucherUploaded && (
                  <span className="text-xs text-green-700">
                    已上传：{payVoucherUploaded.originalName ?? '凭证'} (
                    {payVoucherUploaded.byteSize} bytes)
                  </span>
                )}
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-gray-600">付款流水号（可选）</label>
              <input
                type="text"
                value={payReference}
                onChange={(e) => setPayReference(e.target.value)}
                maxLength={120}
                className="w-full rounded-md border border-gray-300 px-2 py-1 text-xs"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-gray-600">备注（可选）</label>
              <textarea
                value={payRemarks}
                onChange={(e) => setPayRemarks(e.target.value)}
                maxLength={500}
                className="h-16 w-full rounded-md border border-gray-300 p-2 text-xs"
              />
            </div>
            {!payVoucherUploaded && (
              <label className="flex items-start gap-2 text-xs text-amber-700">
                <input
                  type="checkbox"
                  checked={payConfirmNoVoucher}
                  onChange={(e) => setPayConfirmNoVoucher(e.target.checked)}
                  className="mt-0.5"
                />
                <span>
                  确认无凭证付款（仅限现金等无即时凭证场景；该笔付款将留空凭证 id，
                  事后补凭证需要走人工流程）
                </span>
              </label>
            )}
            {rowActions.actionError && (
              <div className="rounded-md bg-red-50 px-2 py-1 text-xs text-red-700">
                {rowActions.actionError.message}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => closePayDialog()}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm"
                disabled={rowActions.actionLoading}
              >
                关闭
              </button>
              <button
                type="button"
                disabled={
                  rowActions.actionLoading ||
                  (!payVoucherUploaded && !payConfirmNoVoucher)
                }
                onClick={() => void handleConfirmPay()}
                className="rounded-md bg-purple-600 px-3 py-1.5 text-sm text-white disabled:opacity-50"
              >
                确认付款
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
